"use client";

import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  APP_UPDATE_EVENT,
  type AppUpdateReason,
  normalizeBuildId,
} from "@/lib/app-update";

const CURRENT_BUILD_ID = normalizeBuildId(process.env.NEXT_PUBLIC_APP_BUILD_ID);
const VERSION_ENDPOINT = "/api/version";
const VERSION_CHECK_INTERVAL_MS = 60_000;

type VersionResponse = {
  buildId: string | null;
};

export function AppUpdateNotifier() {
  const [updateReason, setUpdateReason] = useState<AppUpdateReason | null>(null);

  useEffect(() => {
    let disposed = false;

    async function checkForUpdate() {
      if (disposed || updateReason || !CURRENT_BUILD_ID) {
        return;
      }

      if (document.visibilityState === "hidden") {
        return;
      }

      try {
        const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
          cache: "no-store",
        });

        if (!response.ok) {
          return;
        }

        const contentType = response.headers.get("content-type") ?? "";
        if (!contentType.includes("application/json")) {
          return;
        }

        const data = (await response.json()) as VersionResponse;
        const deployedBuildId = normalizeBuildId(data.buildId);

        if (
          deployedBuildId &&
          CURRENT_BUILD_ID &&
          deployedBuildId !== CURRENT_BUILD_ID
        ) {
          setUpdateReason("version-mismatch");
        }
      } catch {
        // Ignore transient network failures; the next poll/focus check will retry.
      }
    }

    function handleAppUpdateRequired(event: Event) {
      const customEvent = event as CustomEvent<AppUpdateReason>;
      setUpdateReason(customEvent.detail ?? "stale-build-error");
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void checkForUpdate();
      }
    }

    function handleFocus() {
      void checkForUpdate();
    }

    void checkForUpdate();

    const intervalId = window.setInterval(checkForUpdate, VERSION_CHECK_INTERVAL_MS);

    window.addEventListener(APP_UPDATE_EVENT, handleAppUpdateRequired as EventListener);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener(APP_UPDATE_EVENT, handleAppUpdateRequired as EventListener);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [updateReason]);

  if (!updateReason) {
    return null;
  }

  const message =
    updateReason === "stale-build-error"
      ? "Esta pestaña quedó desactualizada después de una publicación. Recarga la página para continuar sin errores."
      : "Hay una nueva versión disponible. Recarga la página para usar la versión más reciente.";

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex justify-center px-4">
      <div
        aria-live="polite"
        className="pointer-events-auto flex w-full max-w-2xl items-center justify-between gap-4 rounded-xl border bg-background/95 p-4 shadow-lg backdrop-blur"
        role="status"
      >
        <div className="space-y-1">
          <p className="text-sm font-semibold">Nueva versión disponible</p>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>

        <Button
          className="shrink-0"
          onClick={() => window.location.reload()}
          type="button"
        >
          <RefreshCw className="h-4 w-4" />
          Actualizar ahora
        </Button>
      </div>
    </div>
  );
}
